/* eslint-disable */
import { FocusEligibilityFinder } from './focusEligibilityFinder';
import { FocusTrap } from './focusTrap';

/**
 * General Helper for accessibility concerns in classes
 *
 * Usage Example:

  import { AccessibilityHelper } from 'accessibility/accessibilityHelper';

  class Foo {
    constructor() {
      this.dom = {
        $frames: document.querySelectorAll('.js-frame')
      }
      this.state = {
        open: false,
        accessibilityHelper: new AccessibilityHelper,
        modal = document.querySelector('.js-modal-with-focus-elements'),
        modalToggle = document.querySelector('.js-modal-toggle')
      }
      this.closeHandler = this.closeOverlay.bind(this);
    }

    mount() {
      const { state: { accessibilityHelper }, closeHandler } = this;

      initFocusTrap();
    }

    initFocusTrap() {
      const { state: { accessibilityHelper, modal, modalToggle } } = this;

      state.focusTrap = accessibilityHelper.buildFocusTrap([modal, modalToggle])
    }

    openOverlay() {
      const { state: { focusTrap } } = this;

      // trap focus to the overlay and the toggle
      // when the overlay is open
      focusTrap.set()
    }

    closeOverlay() {
      const { state: { focusTrap } } = this;

      // remove the focus trap when overlay is closed
      focusTrap.unset()
    }

    updateActiveFrame(activeFrame) {
      const { state: { accessibilityHelper }, dom: { $frames } } = this;

      // hide inactive frames that should not be visible/tabbable
      for (let frame of $frames) {
        accessibilityHelper.updateElementAccessibility(frame, false);
      }

      // show active frame that should be visible/tabbable
      accessibilityHelper.updateElementAccessibility(activeFrame, true);
    }
  }

* */
class AccessibilityHelper {
  constructor() {
  }

  /**
   * Returns FocusTrap instance to traps focus to an element/element with focusable elements
   *
   * @param {Node | Node[]} $elements A single node/array of nodes whose child elements/self will be focused on
   *
   * Usage Example:
   * let modal = document.querySelector('.js-modal-with-focus-elements');
   * let modalToggle = document.querySelector('.js-modal-toggle'); //optional
   * let focustrapInstance = accessibilityHelper.buildFocusTrap([modal, modalToggle])
   *
   * focustrapInstance.set(); //Tabbing focus is now trapped to elements within modal and toggle
   * focustrapInstance.unset(); //Tabbing focus is removed and entire DOM can be traversed using tab
  * */
  buildFocusTrap($elements) {
    return new FocusTrap($elements);
  }

  /**
   * Sets 'aria-hidden' and 'tabindex' attributes on an element or group of elements
   * based on whether the elements should be accessible (tab-accessible, visible to screen
   * readers, etc) or not
   *
   * @param { Array } element_or_array an element or group of elements
   * @param { Boolean } isAccessible whether the elements should be accessible or not
   *
   * Usage Example:
   * for (let frame of dom.$frames) {
   *   accessibilityHelper.updateElementAccessibility(frame, false);
   * }
   *
   * accessibilityHelper.updateElementAccessibility(activeFrame, true);
   *
  * */
  updateElementAccessibility(element_or_array, isAccessible) {
    const $elements = Array.isArray(element_or_array) ? element_or_array : [element_or_array];
    let $focusableContent = [];
    const focusEligibilityFinder = new FocusEligibilityFinder();
    const tabIndex = isAccessible ? '0' : '-1';

    $elements.forEach(($element) => {
      const $focusableElements = focusEligibilityFinder.findFrom($element);

      $focusableContent = [
        ...$focusableContent,
        ...$focusableElements,
      ];
    });

    $focusableContent.forEach((element) => {
      element.setAttribute('aria-hidden', !isAccessible);
      element.setAttribute('tabindex', tabIndex);
    });
  }

  addEscapeKeyListener(callback) {
    
    this.escapeKeyHandler = (e) => {
      if (e.keyCode === 27) {
        callback();
      }
    };

    document.addEventListener('keydown', this.escapeKeyHandler);
  }

  removeEscapeKeyListener() {
    document.removeEventListener('keydown', this.escapeKeyHandler);
  }
}

export { AccessibilityHelper };
